import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { supabase } from './lib/supabase';
import { Shell } from './components/Shell';
import { Login } from './pages/Login';
import { Onboarding } from './pages/Onboarding';
import { AcceptInvite } from './pages/AcceptInvite';
import { Leads } from './pages/Leads';
import { Projects } from './pages/Projects';
import { Team } from './pages/Team';
import { Links } from './pages/Links';
import { Simulator } from './pages/Simulator';
import { ProjectDetail } from './pages/ProjectDetail';
import { Review } from './pages/Review';
import { Settings } from './pages/Settings';
import { RepToday } from './pages/RepToday';
import { Dashboard } from './pages/Dashboard';
import { Posts } from './pages/Posts';
import { Money } from './pages/Money';
import { ProofPack } from './pages/ProofPack';
import { Home } from './pages/Home';
import { AgentStudio } from './pages/AgentStudio';

function Boot({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center px-4 text-center text-muted">{children}</div>;
}

// Signed in and a member of a firm, or sent where they need to go.
function RequireMember({ children }: { children: ReactNode }) {
  const { loading, session, me } = useAuth();
  const loc = useLocation();
  if (loading) return <Boot>Loading…</Boot>;
  if (!session) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (!me) return <Onboarding />;
  return <>{children}</>;
}

function ManagerOnly({ children }: { children: ReactNode }) {
  const { isManager } = useAuth();
  if (!isManager) return <p className="text-muted">Only managers and owners can open this page.</p>;
  return <>{children}</>;
}

function LoginRoute() {
  const { loading, session } = useAuth();
  if (loading) return <Boot>Loading…</Boot>;
  if (session) return <Navigate to="/today" replace />;
  return <Login />;
}

function HomeRoute() {
  const { session } = useAuth();
  if (session) return <Navigate to="/today" replace />;
  return <Home />;
}

function InviteRoute() {
  const { loading } = useAuth();
  if (loading) return <Boot>Loading…</Boot>;
  return <AcceptInvite />;
}

export function App() {
  if (!supabase) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="*" element={<Boot>Sign-in is not connected to a database yet. <a className="ml-1 text-accent underline" href="/">Return to ReAstra home.</a></Boot>} />
        </Routes>
      </BrowserRouter>
    );
  }
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/invite/:token" element={<InviteRoute />} />
          <Route path="/proof/:id" element={<RequireMember><ProofPack /></RequireMember>} />
          <Route element={<RequireMember><Shell /></RequireMember>}>
            <Route path="/today" element={<RepToday />} />
            <Route path="/dashboard" element={<ManagerOnly><Dashboard /></ManagerOnly>} />
            <Route path="/posts" element={<Posts />} />
            <Route path="/money" element={<ManagerOnly><Money /></ManagerOnly>} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/:id" element={<ProjectDetail />} />
            <Route path="/review" element={<Review />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/team" element={<Team />} />
            <Route path="/links" element={<Links />} />
            <Route path="/simulator" element={<ManagerOnly><Simulator /></ManagerOnly>} />
            <Route path="/agent" element={<ManagerOnly><AgentStudio /></ManagerOnly>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
